/**
 * 配車画面の左右リストの幅。ドラッグで変えた幅を localStorage に残し、次回も同じ幅で開く。
 *
 * 上下限はサンプル (route-planner: pages/MapPage.tsx startLeftResize) と同じ。
 * Why 新設: 旧地図配車 (V3Plans/Presenter.tsx:387,407) は左 430・右 300 の固定値で、
 * 開閉で 0 に切り替えるだけのリサイズ機構しか持たない。流用できる実装が無い。
 *
 * Why ここ (components/dispatch) に置く: /map (log_web_issues#299) と
 * カード配車 (#312) が同じ左右パネル (ConvenienceDispatchVehicleList /
 * ConvenienceDispatchUnassignedList) を同じ規則で可変にする。とくに fitPanelWidths の
 * 跳ね防止はレビューで 3 度指摘されて固めた計算なので、二重に持つと片方だけ直す事故になる。
 *
 * 既定幅と localStorage キーは画面ごとに違う（初回の見た目を変えないため現状の固定値を
 * 引き継ぐ）ので、各画面の panelWidth.ts が持つ。
 */
export const MIN_PANEL_WIDTH = 260;
export const MAX_PANEL_WIDTH = 720;

/** リサイズハンドルの幅。左右 1 本ずつ置く */
export const RESIZE_HANDLE_WIDTH = 6;

/**
 * 左右に挟まれた中央ペインに残す最小幅。左右を独立して上限まで広げると中央が潰れる。
 * 幅は localStorage に残るため、一度潰すとリロードしても戻らない。
 * 1366px の画面で片側が既定幅なら反対側は上限 720px まで伸ばせる値にしてある。
 *
 * - /map: 地図は flex: 1・minWidth: 0 なので文字通り幅 0 になる
 * - カード配車: 中央ボードは横スクロールするが、左カラムが sticky left: 0 なので
 *   可視幅が左カラムより狭くなるとボードが完全に隠れ、横スクロールでも出せなくなる
 *
 * カード幅は 160px (ConvenienceDispatchBoard の CARD_WIDTH) なので、240px あれば
 * 1 枚ぶんと次の列の頭が見える。
 */
export const MIN_CENTER_WIDTH = 240;

/**
 * このパネル以外に必要な幅（反対側のパネル＋ハンドル＋中央ペインの最小幅）。
 * ドラッグ中の上限をここから逆算する。
 *
 * ハンドルの本数は fitPanelWidths と同じ数え方にする。右ペインを畳んでいる間は
 * 右ハンドルを描かない（Presenter が条件付きで描く）ので 1 本。2 本固定にしていたときは、
 * 畳んでいる間だけドラッグが描画時の補正より 6px 手前で止まっていた。
 *
 * 右ハンドル自身の呼び出しは「右が開いている＝両方描かれている」ときにしか起きないので、
 * 既定の true をそのまま使う。
 */
export function reservedWidthFor(otherPanelWidth: number, otherPanelOpen = true): number {
  return otherPanelWidth + RESIZE_HANDLE_WIDTH * (otherPanelOpen ? 2 : 1) + MIN_CENTER_WIDTH;
}

/** ドラッグ中の幅を上下限へ収める。端数は 1px 未満のちらつきになるので丸める */
export function clampPanelWidth(width: number): number {
  return Math.round(Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, width)));
}

/**
 * 器（3 ペインの親）へ収まる幅に落とす。保存値そのものは書き換えない。
 *
 * Why 描画時にも要る: ドラッグ開始時の上限だけでは
 *   右を広げる → 右を畳む → 左を広げる → 右を開き直す
 * の順で左右とも大きな保存値を持ててしまい、中央ペインが幅 0 になる。幅は localStorage に
 * 残るのでリロードしても直らない。画面を狭めた場合・広い画面で保存した幅を狭い画面で
 * 開いた場合も同じ。
 *
 * Why not 保存値を書き換える: 画面を一時的に狭めただけで設定を失う。描画時に落とすだけに
 * すれば、広げ直したときに元の幅へ戻る。
 *
 * ## 削り方は「左を優先して残す」
 * 超過分を今の幅の比で削る実装にしていたときは、片側の保存値だけが変わると分母が動いた。
 * 補正中の左を 1px 右へドラッグすると（表示値 388 が保存される）左が 43px 縮んで触っていない
 * 右が広がる、というドラッグの向きと逆の跳ねが出た（Codex 指摘）。
 * 左の補正結果を右の保存値から独立させることで、この跳ねが原理的に起きないようにする。
 * 左を優先するのは、依頼の主題が左リストで納品先名称を読み切ることだから。
 */
export function fitPanelWidths(params: {
  /** 3 ペインを並べている親要素の幅。測れていなければ 0 */
  containerWidth: number;
  left: number;
  right: number;
  /** 右ペインが開いているか。畳んでいる間は右の幅を数えない */
  rightOpen: boolean;
}): { left: number; right: number } {
  const { containerWidth, left, right, rightOpen } = params;
  if (containerWidth <= 0) return { left, right };

  // ハンドルは右を畳んでいる間 1 本しか描かない（Presenter 側と同じ条件）
  const available = containerWidth - RESIZE_HANDLE_WIDTH * (rightOpen ? 2 : 1) - MIN_CENTER_WIDTH;

  if (!rightOpen) {
    return { left: Math.max(MIN_PANEL_WIDTH, Math.min(left, available)), right };
  }

  if (left + right <= available) return { left, right };

  // 左は「右に下限を残せる範囲」まで保つ。ここで右の保存値を見ないのが跳ね防止の要点
  const fittedLeft = Math.max(MIN_PANEL_WIDTH, Math.min(left, available - MIN_PANEL_WIDTH));
  // 残りを右に回す。両方下限でも入らない画面では下限で止める
  // （中央ペインの最小幅を諦める。パネルを消すほうが困る）
  const fittedRight = Math.max(MIN_PANEL_WIDTH, Math.min(right, available - fittedLeft));

  return { left: fittedLeft, right: fittedRight };
}

/**
 * localStorage 復元時のバリデータ（usePersistedJSON 用）。
 *
 * Why not 範囲外を clamp して救う: 上下限を変えたときに古い保存値が黙って新しい境界へ
 * 張り付く。既定へ戻せば「なぜその幅なのか」が読める。
 */
export function isPanelWidth(parsed: unknown): parsed is number {
  return typeof parsed === 'number'
    && Number.isFinite(parsed)
    && parsed >= MIN_PANEL_WIDTH
    && parsed <= MAX_PANEL_WIDTH;
}
